<template>
  <page-list
    :list="list"
    :columns="columns"
    :export-data-options="exportDataOptions"
    :singleActions="singleActions">
    <template v-slot:group-actions-append>
      <a-radio-group v-model="dateMode" class="ml-2">
        <a-radio-button v-for="item in timeOpts" :key="item.key" :value="item.key">{{ item.label }}</a-radio-button>
        <custom-date :customDate.sync="customDate" :time.sync="dateMode" :customTimeLabel="customTimeLabel" canSelectTodayAfter />
      </a-radio-group>
    </template>
  </page-list>
</template>

<script>
import WindowsMixin from '@/mixins/windows'
import ListMixin from '@/mixins/list'
import {
  getNameDescriptionTableColumn,
  getProjectDomainTableColumn,
} from '@/utils/common/tableColumn'
import CustomDate from '@/components/DateTime/CustomDate'
import { numerify } from '@/filters'

export default {
  name: 'BillPredictConfigList',
  components: {
    CustomDate,
  },
  mixins: [WindowsMixin, ListMixin],
  props: {
    id: String,
    getParams: {
      type: Object,
      default: () => ({ details: true }),
    },
  },
  data () {
    return {
      dateMode: '30day_near',
      customDate: {},
      customTimeLabel: '',
      list: this.$list.createList(this, {
        id: this.id,
        resource: 'predictions/query',
        apiVersion: 'v1',
        idKey: 'tenant_id',
        getParams: this.getParam,
        noPreLoad: true,
        filterOptions: {
          project: {
            label: this.$t('bill.project_name'),
            filter: true,
            formatter: val => {
              return `project.contains('${val}')`
            },
          },
        },
      }),
      columns: [
        getNameDescriptionTableColumn({
          onManager: this.onManager,
          hideField: true,
          edit: false,
          editDesc: false,
          showDesc: false,
          sortable: false,
          title: this.$t('bill.project_name'),
          field: 'project',
          slotCallback: row => {
            return (
              <side-page-trigger onTrigger={ () => this.handleOpenSidepage(row) }>{ row.project }</side-page-trigger>
            )
          },
        }),
        {
          field: 'amount',
          title: this.$t('bill.amount_fee'),
          sortable: true,
          sortBy: 'order_by_amount',
          formatter: ({ row }) => {
            return numerify(row.amount, '0.00')
          },
          needExportFormat: true,
        },
        {
          field: 'prediction',
          title: this.$t('bill.predict_fee'),
          sortable: true,
          sortBy: 'order_by_prediction',
          formatter: ({ row }) => {
            return numerify(row.prediction, '0.00')
          },
          needExportFormat: true,
        },
        getProjectDomainTableColumn({ sortable: false, field: 'domain' }),
      ],
    }
  },
  computed: {
    exportDataOptions () {
      return {
        title: this.$t('dictionary.project'),
        downloadType: 'local',
        items: this.columns,
        needExportFormat: true,
      }
    },
    timeOpts () {
      return [
        { key: '30day_near', label: this.$t('bill.predict.near_30day') },
        { key: '90day_near', label: this.$t('bill.predict.near_90day') },
        { key: '6month', label: this.$t('bill.predict.future_6month') },
        { key: '1year', label: this.$t('bill.predict.future_1year') },
        { key: '2year', label: this.$t('bill.predict.future_2year') },
        { key: '5year', label: this.$t('bill.predict.future_5year') },
      ]
    },
  },
  watch: {
    dateMode: {
      immediate: true,
      handler (val) {
        this.updateCustomTimeLabel(val, this.customDate)
        this.fetchData()
      },
    },
    customDate: {
      deep: true,
      handler (val) {
        this.updateCustomTimeLabel(this.dateMode, val)
        this.fetchData()
      },
    },
  },
  created () {
    this.fetchData()
  },
  methods: {
    getParam () {
      const ret = {
        'group.0': 'tenant_id',
      }
      if (this.dateMode === '30day_near') {
        ret.start_day = parseInt(this.$moment().subtract(30, 'day').format('YYYYMMDD'))
        ret.end_day = parseInt(this.$moment().subtract(1, 'day').format('YYYYMMDD'))
        // ret['group.0'] = 'day'
      } else if (this.dateMode === '90day_near') {
        ret.start_day = parseInt(this.$moment().subtract(90, 'day').format('YYYYMMDD'))
        ret.end_day = parseInt(this.$moment().subtract(1, 'day').format('YYYYMMDD'))
        // ret['group.0'] = 'month'
      } else if (this.dateMode === '6month') {
        ret.start_day = parseInt(this.$moment().add(1, 'month').startOf('month').format('YYYYMMDD'))
        ret.end_day = parseInt(this.$moment().add(6, 'month').endOf('month').format('YYYYMMDD'))
        // ret['group.0'] = 'month'
      } else if (this.dateMode === '1year') {
        ret.start_day = parseInt(this.$moment().add(1, 'year').startOf('year').format('YYYYMMDD'))
        ret.end_day = parseInt(this.$moment().add(1, 'year').endOf('year').format('YYYYMMDD'))
        // ret['group.0'] = 'month'
      } else if (this.dateMode === '2year') {
        ret.start_day = parseInt(this.$moment().add(1, 'year').startOf('year').format('YYYYMMDD'))
        ret.end_day = parseInt(this.$moment().add(2, 'year').endOf('year').format('YYYYMMDD'))
        // ret['group.0'] = 'month'
      } else if (this.dateMode === '5year') {
        ret.start_day = parseInt(this.$moment().add(1, 'year').startOf('year').format('YYYYMMDD'))
        ret.end_day = parseInt(this.$moment().add(5, 'year').endOf('year').format('YYYYMMDD'))
        // ret['group.0'] = 'month'
      } else if (this.dateMode === 'custom') {
        const { start, end } = this.customDate
        ret.start_day = parseInt(start.format('YYYYMMDD'))
        ret.end_day = parseInt(end.format('YYYYMMDD'))
        // ret['group.0'] = 'month'
        // const diffMonth = this.$moment(end).add(1, 'seconds').diff(start, 'months') // 相差几个月
        // if (diffMonth <= 1) ret['group.0'] = 'day'
      }
      return ret
    },
    fetchData () {
      this.list.fetchData()
    },
    handleOpenSidepage (row, tab) {
      this.sidePageTriggerHandle(this, 'PredictConfigSidePage', {
        id: row.id,
        resource: () => {
          return {
            data: row,
          }
        },
        data: row,
      }, {
        list: this.list,
        tab,
        dateMode: this.dateMode,
        customDate: this.customDate,
      })
    },
    updateCustomTimeLabel (type, date) {
      try {
        if (type === 'custom') {
          const start = this.$moment(date.start).format('YYYY-MM-DD')
          const end = this.$moment(date.end).format('YYYY-MM-DD')
          this.customTimeLabel = ` (${start} - ${end})`
        } else {
          this.customTimeLabel = ''
        }
      } catch (err) {
        this.customTimeLabel = ''
      }
    },
  },
}
</script>
